import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Button, Skeleton } from 'antd';
import PublicWebContentService from '../services/publicWebContentService';
import './Home.css';
import './About.css';

const { Title, Paragraph } = Typography;

const STORY_MILESTONES = [
  {
    year: '2018',
    text: 'Current Care founded with a vision to provide quality electrical services.',
  },
  { year: '2019', text: 'Expanded product range to include consumer electronics.' },
  { year: '2020', text: 'Launched comprehensive repair services during high demand.' },
  { year: '2021', text: 'Introduced mobile repair vans for on-site service.' },
  { year: '2022', text: 'Reached 50,000+ satisfied customers milestone.' },
  { year: '2023', text: 'Expanded nationwide with new service centers.' },
];

const About = () => {
  const [loading, setLoading] = useState(true);
  const [storyTitle, setStoryTitle] = useState('Our Story');
  const [storyDescription, setStoryDescription] = useState(
    'Follow our journey from a small startup to a trusted leader in electrical and electronics services.',
  );
  // Helper to convert section array to keyed map
  const sectionToMap = (items) => {
    const map = {};
    (items || []).forEach((item) => {
      map[item.content_key] = item.content_value;
    });
    return map;
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        const pageContent = await PublicWebContentService.getPageContent('about');
        if (pageContent) {
          const story = sectionToMap(pageContent.story);

          if (story.title) setStoryTitle(story.title);
          if (story.description) setStoryDescription(story.description);
        }
      } catch (error) {
        console.error('Failed to load about content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '40px' }}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    );
  }

  return (
    <div className="about-page">
      {/* Our Story */}
      <section className="bg-surface">
        <div className="home-section">
          <div className="section-header">
            <Title level={2}>{storyTitle}</Title>
            <Paragraph>{storyDescription}</Paragraph>
          </div>
          <Row gutter={[48, 40]} align="middle">
            <Col xs={24} md={10}>
              <div className="story-media">
                <img
                  src="/images/hero-technician.jpg"
                  alt="Current Care technician at work"
                  loading="lazy"
                />
                <div className="story-media-badge">
                  <b>2018</b>
                  <span>Serving Nepal since</span>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14}>
              <ol className="story-timeline">
                {STORY_MILESTONES.map((m) => (
                  <li className="story-item" key={m.year}>
                    <span className="story-year">{m.year}</span>
                    <div className="story-content">
                      <p>{m.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Col>
          </Row>

          {/* Heartfelt note */}
          <div className="story-note">
            <p>
              Current Care began with one simple promise, that every family in Nepal deserves a home
              that feels <span className="accent">safe, warm and truly cared for</span>. Behind
              every wire we fix and every appliance we carry home, there is a real person we are
              looking out for, a parent who wants the lights to stay on, a family gathering around a
              working TV, a small business that cannot afford to slow down.
            </p>
            <p className="story-note-sign">
              That is the <b>care</b> in Current Care, and it is why our customers trust us like
              family.
            </p>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-400">
        <div className="mx-auto max-w-content px-6 py-16 text-center">
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">
            Ready to power up your home?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-brand-100">
            Talk to our team or explore our services, reliable products and expert repairs, all in
            one place.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button
              size="large"
              href="/contact"
              style={{
                height: 48,
                paddingInline: 26,
                fontWeight: 600,
                background: '#fe9c07',
                borderColor: '#fe9c07',
                color: '#08152b',
              }}
            >
              Contact Us
            </Button>
            <Button
              size="large"
              ghost
              href="/services"
              style={{
                height: 48,
                paddingInline: 24,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
              }}
            >
              Our Services
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
